import { Fragment, useCallback } from 'react';

import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import FilterWrapper from 'src/components/Search/Common/Filters/FilterWrapper';
import { useApplyFilter } from 'src/components/Search/Common/Filters/hooks/useApplyFilter';
import { useUpdateFilter } from 'src/components/Search/Common/Filters/hooks/useUpdateFilter';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { ClusterKey } from 'src/types/search/common/clusters';
import { getLanguageLevel } from 'src/utils/resume/getLanguageLevel';

import AddLink from 'src/components/Search/Resume/Filters/Language/AddLink';
import FormItemWithVerification from 'src/components/Search/Resume/Filters/Language/FormItemWithVerification';
import RemoveLink from 'src/components/Search/Resume/Filters/Language/RemoveLink';

const TrlKeys = {
    title: 'novafilters.language',
    addVerified: 'clusters.verifiedLanguages',
};

const Language: TranslatedComponent = ({ trls }) => {
    const applyLanguageFilter = useApplyFilter(ClusterKey.Language);
    const updateVerifiedLanguagesFilter = useUpdateFilter(ClusterKey.VerifiedLanguages);

    const languages = useSelector((state) => state.languages);
    const selectedLanguages = useSelector((state) => state.searchClusters[ClusterKey.Language].selectedValues);
    const selectedVerifiedLanguages = useSelector(
        (state) => state.searchClusters[ClusterKey.VerifiedLanguages].selectedValues
    );

    const updateValue = useCallback(
        (oldLanguage: string, newLanguage: string) =>
            applyLanguageFilter(
                selectedLanguages.map((language) => (language === oldLanguage ? newLanguage : language))
            ),
        [applyLanguageFilter, selectedLanguages]
    );

    const removeValue = useCallback(
        (removingLanguage: string) =>
            applyLanguageFilter(selectedLanguages.filter((language) => language !== removingLanguage)),
        [applyLanguageFilter, selectedLanguages]
    );

    return (
        <FilterWrapper filterKey={ClusterKey.Language} title={trls[TrlKeys.title]}>
            {selectedLanguages.map((languageSection) => {
                const searchId = getLanguageLevel({ languages: languages.language, record: languageSection });
                return (
                    <Fragment key={languageSection}>
                        <FormItemWithVerification
                            languageSection={languageSection}
                            selectedLanguages={selectedLanguages}
                            preTitleDataQa="resume-search"
                            updateValue={updateValue}
                            removeValue={removeValue}
                        />
                        <RemoveLink
                            onClick={() => {
                                updateVerifiedLanguagesFilter(
                                    selectedVerifiedLanguages.filter((language) => language !== searchId)
                                );
                                removeValue(languageSection);
                            }}
                            dataQa="resume-search-delete-language"
                        />
                        <input type="hidden" name="language" value={languageSection} />
                    </Fragment>
                );
            })}
            <AddLink value={selectedLanguages} onChange={applyLanguageFilter} preTitleDataQa="resume-search" />
        </FilterWrapper>
    );
};

export default translation(Language);
